import { useCallback, useMemo, useState } from 'react'; import { useTranslation } from 'react-i18next';
import { IconAddressBook } from '@tabler/icons-react';
import { CrudMode, renderMultiListToTags, CrudSchemaFields, type EnsoFormItem } from '@ohpen/enso-ui';
import { Tag, Form } from 'antd';

import { TabProps } from '../interfaces';
import { CrudFormCard } from './CrudFormCard';
import { AddressField } from './AddressField';
import { getHighlightProps } from '../utils';
const QUALIFIED_DOMAINS_STORAGE_KEY = 'firms:qualified-domains';

export const ContactDetails: React.FC<TabProps> = ({ form, mode, fcaModifiedFields, removeFcaModifiedField }) => {
  const { t } = useTranslation();
  const readonly = mode === CrudMode.VIEW;
  const type = Form.useWatch(['registrationDetails', 'type'])

  const [knownDomains, setKnownDomains] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(QUALIFIED_DOMAINS_STORAGE_KEY);
      return stored ? (JSON.parse(stored) as string[]) : [];
    } catch {
      return [];
    }
  });

  const onQualifiedDomainsChange = useCallback((values: string[]) => {
    setKnownDomains(prev => {
      const merged = Array.from(new Set([...prev, ...values]));
      localStorage.setItem(QUALIFIED_DOMAINS_STORAGE_KEY, JSON.stringify(merged));
      return merged;
    });
  }, []);

  const isFcaRegistered = useMemo(() => type !== 'NOT_FCA_REGISTERED', [type]);

  const columns: EnsoFormItem[] = useMemo(
    () => [
      {
        valueType: 'group',
        columns: [
          {
            title: t('lender.firms.labels.mobile'),
            dataIndex: ['contactDetails', 'mobilePhone'],
            valueType: 'text',
            fieldProps: {
              className: getHighlightProps(['contactDetails', 'mobilePhone'], fcaModifiedFields),
              onBlur: () => removeFcaModifiedField?.('contactDetails', 'mobilePhone'),
            },
            formItemProps: {
              label: (
                <span>
                  {t('lender.firms.labels.mobile')}
                  {isFcaRegistered && (
                    <Tag color="blue" style={{ marginLeft: 8 }}>
                      {t('lender.firms.labels.fca')}
                    </Tag>
                  )}
                </span>
              ),
            },
            readonly,
            colProps: { span: 12 },
          },
          {
            title: t('lender.firms.labels.telephone'),
            dataIndex: ['contactDetails', 'workPhone'],
            valueType: 'text',
            fieldProps: {
              className: getHighlightProps(['contactDetails', 'workPhone'], fcaModifiedFields),
              onBlur: () => removeFcaModifiedField?.('contactDetails', 'workPhone'),
            },
            formItemProps: {
              label: (
                <span>
                  {t('lender.firms.labels.telephone')}
                  {isFcaRegistered && (
                    <Tag color="blue" style={{ marginLeft: 8 }}>
                      {t('lender.firms.labels.fca')}
                    </Tag>
                  )}
                </span>
              ),
            },
            readonly,
            colProps: { span: 12 },
          },
        ],
      },
      {
        valueType: 'group',
        columns: [
          {
            title: t('lender.firms.labels.website'),
            dataIndex: ['contactDetails', 'website'],
            valueType: 'text',
            fieldProps: {
              className: getHighlightProps(['contactDetails', 'website'], fcaModifiedFields),
              onBlur: () => {
                removeFcaModifiedField?.('contactDetails', 'website');
              },
            },
            formItemProps: {
              label: (
                <span>
                  {t('lender.firms.labels.website')}
                  {isFcaRegistered && (
                    <Tag color="blue" style={{ marginLeft: 8 }}>
                      {t('lender.firms.labels.fca')}
                    </Tag>
                  )}
                </span>
              ),
            },
            readonly,
            colProps: { span: 12 },
          },
          {
            title: t('lender.firms.labels.complaintsWebPage'),
            dataIndex: ['contactDetails', 'complaintsWebsite'],
            valueType: 'text',
            readonly,
            colProps: { span: 12 },
          },
          {
            title: t('lender.firms.labels.email'),
            dataIndex: ['contactDetails', 'email'],
            valueType: 'text',
            readonly,
            formItemProps: {
              rules: [{ required: true }],
            },
            colProps: { span: 12 },
          },
        ],
      },
      {
        title: t('lender.firms.labels.qualifiedDomains'),
        dataIndex: ['contactDetails', 'qualifiedDomains'],
        valueType: 'select',
        readonly,
        render: renderMultiListToTags,
        fieldProps: {
          mode: 'tags',
          tokenSeparators: [','],
          notFoundContent: null,
          options: knownDomains.map(d => ({ label: d, value: d })),
          onChange: onQualifiedDomainsChange,
        },
        formItemProps: {
          tooltip: readonly ? null : t('lender.firms.labels.qualifiedDomainsTooltip'),
        },
        colProps: { span: 12 },
      },
    ],
    [
      readonly,
      t,
      fcaModifiedFields,
      removeFcaModifiedField,
      isFcaRegistered,
      knownDomains,
      onQualifiedDomainsChange,
    ],
  );

  return (
    <CrudFormCard icon={<IconAddressBook />} title={t('lender.firms.labels.contactDetails')}>
      <CrudSchemaFields schema={columns} form={form} readonly={readonly} component={false} />
      <AddressField
        readonly={readonly}
        form={form}
        getAddress={() => form?.getFieldValue(['contactDetails', 'firmAddress'])}
        fcaModifiedFields={fcaModifiedFields}
        removeFcaModifiedField={removeFcaModifiedField}
        isFromFca={isFcaRegistered}
        namePrefix={['contactDetails', 'firmAddress']}
        titleKey="lender.firms.labels.registeredOfficeAddress"
        fcaFieldKey="contactDetails"
        fcaFieldNestedKey="firmAddress"
      />
    </CrudFormCard>
  );
};
